import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { api, API, getToken } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { FileText, Save, Eye, Download, ReceiptText } from "lucide-react";
import { useLang } from "@/context/LanguageContext";
import { FindoSpinner } from "@/components/FindoSpinner";

export const BillingInvoices = ({ partner }) => {
  const { t } = useLang();
  const [form, setForm] = useState({
    org_name: "", country: "FR", address: "", city: "", postal_code: "", siret: "", vat_number: "",
  });
  const [invoices, setInvoices] = useState([]);
  const [saving, setSaving] = useState(false);
  const [busy, setBusy] = useState("");
  const [zipYear, setZipYear] = useState("");

  useEffect(() => {
    if (partner) {
      setForm((f) => ({
        ...f,
        org_name: partner.org_name || "",
        country: (partner.country || "FR").toUpperCase(),
        address: partner.address || "",
        city: partner.city || "",
        postal_code: partner.postal_code || "",
        siret: partner.siret || "",
        vat_number: partner.vat_number || "",
      }));
    }
    api("/auth/me/qonto-invoices").then((r) => {
      const list = r.invoices || [];
      setInvoices(list);
      const years = [...new Set(list.map((i) => (i.created_at || "").slice(0, 4)).filter((y) => /^\d{4}$/.test(y)))].sort().reverse();
      if (years.length) setZipYear((y) => y || years[0]);
    }).catch(() => {});
  }, [partner]);

  const years = [...new Set(invoices.map((i) => (i.created_at || "").slice(0, 4)).filter((y) => /^\d{4}$/.test(y)))].sort().reverse();

  const isFR = (form.country || "").toUpperCase() === "FR";

  async function save() {
    setSaving(true);
    try {
      await api("/auth/me/billing-info", { method: "PUT", body: form });
      toast.success(t("hc.billingInformationSaved"));
    } catch (e) { toast.error(e.message); } finally { setSaving(false); }
  }

  async function openInvoicePdf(inv, mode) {
    const key = `${inv.source_id}:${mode}`;
    setBusy(key);
    try {
      const res = await fetch(`${API}/auth/me/invoices/${inv.source_id}/pdf`, {
        headers: { Authorization: `Bearer ${getToken()}` },
      });
      if (!res.ok) throw new Error(t("hc.unableToRetrieveTheInvoice"));
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      if (mode === "download") {
        const a = document.createElement("a");
        a.href = url;
        a.download = `facture-${inv.number || inv.source_id}.pdf`;
        document.body.appendChild(a);
        a.click();
        a.remove();
      } else {
        window.open(url, "_blank", "noopener");
      }
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch (e) {
      toast.error(e.message || "Erreur lors de l'ouverture de la facture.");
    } finally { setBusy(""); }
  }

  async function exportCsv() {
    setBusy("csv");
    try {
      const res = await fetch(`${API}/auth/me/qonto-invoices/export.csv`, {
        headers: { Authorization: `Bearer ${getToken()}` },
      });
      if (!res.ok) throw new Error("Export impossible.");
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = "findloop-factures.csv";
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch (e) {
      toast.error(e.message || "Erreur lors de l'export CSV.");
    } finally { setBusy(""); }
  }

  async function exportZip() {
    if (!zipYear) return;
    setBusy("zip");
    try {
      const res = await fetch(`${API}/auth/me/qonto-invoices/export.zip?year=${zipYear}`, {
        headers: { Authorization: `Bearer ${getToken()}` },
      });
      if (!res.ok) {
        let msg = "Export ZIP impossible.";
        try { msg = (await res.json()).detail || msg; } catch { /* not json */ }
        throw new Error(msg);
      }
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `findloop-factures-${zipYear}.zip`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch (e) {
      toast.error(e.message || "Erreur lors de l'export ZIP.");
    } finally { setBusy(""); }
  }

  return (
    <div className="space-y-6" data-testid="billing-invoices">
      <div className="rounded-2xl border border-border bg-white p-6">
        <div className="flex items-center gap-2 mb-1">
          <ReceiptText size={18} className="text-findo-indigo" />
          <h3 className="font-display text-lg font-bold text-slate-900">{t("hc.billingInformation")}</h3>
        </div>
        <p className="text-sm text-slate-500 mb-4">
          {t("hc.usedForInvoices")} {isFR
            ? t("hc.siretAndVatNumberRequired")
            : t("hc.intraEuVatNumberOptional")}
        </p>
        <div className="grid sm:grid-cols-2 gap-4">
          <div><Label>{t("hc.companyName")}</Label><Input value={form.org_name} onChange={(e) => setForm({ ...form, org_name: e.target.value })} className="mt-1.5 h-11 rounded-xl" data-testid="billing-org-name" /></div>
          <div><Label>{t("hc.countryIsoCodeEG")}</Label><Input value={form.country} maxLength={2} onChange={(e) => setForm({ ...form, country: e.target.value.toUpperCase() })} className="mt-1.5 h-11 rounded-xl" data-testid="billing-country" /></div>
          <div className="sm:col-span-2"><Label>{t("hc.address")}</Label><Input value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} className="mt-1.5 h-11 rounded-xl" data-testid="billing-address" /></div>
          <div><Label>{t("pay.postal")}</Label><Input value={form.postal_code} onChange={(e) => setForm({ ...form, postal_code: e.target.value })} className="mt-1.5 h-11 rounded-xl" data-testid="billing-postal" /></div>
          <div><Label>{t("hc.city")}</Label><Input value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })} className="mt-1.5 h-11 rounded-xl" data-testid="billing-city" /></div>
          <div><Label>SIRET {isFR && <span className="text-findo-red">*</span>}</Label><Input value={form.siret} onChange={(e) => setForm({ ...form, siret: e.target.value })} placeholder={t("hc.14Digits")} className="mt-1.5 h-11 rounded-xl" data-testid="billing-siret" /></div>
          <div><Label>{t("bill.vatIntra")} {isFR && <span className="text-findo-red">*</span>}</Label><Input value={form.vat_number} onChange={(e) => setForm({ ...form, vat_number: e.target.value.toUpperCase() })} placeholder={isFR ? "FR12345678901" : t("hc.optional3")} className="mt-1.5 h-11 rounded-xl" data-testid="billing-vat" /></div>
        </div>
        <Button onClick={save} disabled={saving} className="mt-5 rounded-xl font-semibold" data-testid="billing-save">
          {saving ? <FindoSpinner size={16} className="mr-1.5" /> : <Save size={16} className="mr-1.5" />} {t("hc.saveBtn")}
        </Button>
      </div>

      <div className="rounded-2xl border border-border bg-white p-6">
        <div className="flex items-center justify-between gap-3 mb-4">
          <div className="flex items-center gap-2">
            <FileText size={18} className="text-findo-indigo" />
            <h3 className="font-display text-lg font-bold text-slate-900">{t("hc.myInvoices")}</h3>
          </div>
          {invoices.length > 0 && (
            <div className="flex items-center gap-2">
              {years.length > 0 && (
                <select
                  value={zipYear}
                  onChange={(e) => setZipYear(e.target.value)}
                  className="h-9 rounded-xl border border-findo-indigo/30 bg-white px-2.5 text-sm text-slate-700 focus:outline-none focus:ring-2 focus:ring-findo-indigo/20"
                  data-testid="billing-zip-year"
                >
                  {years.map((y) => <option key={y} value={y}>{y}</option>)}
                </select>
              )}
              <Button
                type="button" variant="outline" size="sm"
                onClick={exportZip} disabled={busy === "zip" || !zipYear}
                className="rounded-xl h-9 text-findo-indigo border-findo-indigo/30 hover:bg-findo-indigo/5"
                data-testid="billing-export-zip"
              >
                {busy === "zip" ? <FindoSpinner size={14} className="mr-1.5" /> : <Download size={14} className="mr-1.5" />} PDF (ZIP)
              </Button>
              <Button
                type="button" variant="outline" size="sm"
                onClick={exportCsv} disabled={busy === "csv"}
                className="rounded-xl h-9 text-findo-indigo border-findo-indigo/30 hover:bg-findo-indigo/5"
                data-testid="billing-export-csv"
              >
                {busy === "csv" ? <FindoSpinner size={14} className="mr-1.5" /> : <Download size={14} className="mr-1.5" />} CSV
              </Button>
            </div>
          )}
        </div>
        {invoices.length === 0 ? (
          <p className="text-sm text-slate-400" data-testid="billing-no-invoices">{t("hc.noInvoiceYetYourInvoices")}</p>
        ) : (
          <ul className="divide-y divide-border" data-testid="billing-invoice-list">
            {invoices.map((inv) => (
              <li key={inv.source_id} className="flex items-center justify-between gap-3 py-3">
                <div className="min-w-0">
                  <p className="font-semibold text-slate-800 text-sm truncate">{inv.number || inv.description}</p>
                  <p className="text-xs text-slate-500">{inv.description} · {Number(inv.amount_eur || 0).toFixed(2)} € · {(inv.created_at || "").slice(0, 10)}</p>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  <Button
                    type="button" variant="outline" size="sm"
                    onClick={() => openInvoicePdf(inv, "view")}
                    disabled={busy === `${inv.source_id}:view`}
                    className="rounded-xl h-9 text-findo-indigo border-findo-indigo/30 hover:bg-findo-indigo/5"
                    data-testid={`billing-invoice-view-${inv.source_id}`}
                  >
                    {busy === `${inv.source_id}:view` ? <FindoSpinner size={14} className="mr-1.5" /> : <Eye size={14} className="mr-1.5" />} {t("hc.view")}
                  </Button>
                  <Button
                    type="button" size="sm"
                    onClick={() => openInvoicePdf(inv, "download")}
                    disabled={busy === `${inv.source_id}:download`}
                    className="rounded-xl h-9 font-semibold"
                    data-testid={`billing-invoice-download-${inv.source_id}`}
                  >
                    {busy === `${inv.source_id}:download` ? <FindoSpinner size={14} className="mr-1.5" /> : <Download size={14} className="mr-1.5" />} {t("hc.download")}
                  </Button>
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
};
